import React from "react";
import { Link } from "react-router-dom";
import { Hero as FundraisingHero } from "../../pages/MarketingHome";
import { HowItWorksSection } from "../../components/home/HowItWorksSection";
import { EarningsFactsSection } from "../../components/home/EarningsFactsSection";
import { BenefitsSection } from "../../components/home/BenefitsSection";
import { Button, SectionHeading } from "../../components/ui";
import { SEO_LINKS } from "../../seo/siteMetadata.js";
import { Reveal, RevealGroup, RevealItem, RevealStagger } from "../../components/motion/Reveal";
import { PUBLIC_MOTION } from "../../components/motion/publicMotion";

const audienceCards = [
  {
    title: "Klassförsäljning",
    text: "Klassförsäljning för klassresa och gemensamma aktiviteter.",
    to: "/klassforsaljning",
  },
  {
    title: "Lagförsäljning",
    text: "Försäljning till cup, läger, utrustning och lagkassa.",
    to: "/tjana-pengar-till-laget",
  },
  {
    title: "Föreningsförsäljning",
    text: "Upplägg för föreningens lag och grupper.",
    to: "/tjana-pengar-till-foreningen",
  },
];

const furtherReadingLinks = SEO_LINKS.filter((link) =>
  ["/provision-och-fortjanst", "/vanliga-fragor"].includes(link.to),
);

export default function HomeContent() {
  return (
    <div className="bg-slate-50">
      <FundraisingHero
        eyebrow="För skolklasser, idrottslag och föreningar"
        title="Samla in pengar till klassen, laget eller föreningen"
        subtitle="SalesPartner är en digital gruppförsäljning med personliga säljlänkar. Välj upplägg för klass, lag eller förening och gå vidare till rätt sida."
        secondaryCtaLabel="Se hur hela flödet fungerar steg för steg"
      />

      <section className="bg-white py-16 md:py-20">
        <RevealGroup className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealItem tone="text">
            <SectionHeading
              eyebrow="Tre vägar in"
              title="Välj rätt upplägg"
              align="left"
            />
          </RevealItem>
          <RevealItem tone="text">
            <p className="max-w-2xl text-neutral-600 text-base md:text-lg">
              Klassförsäljning, lagförsäljning och föreningsförsäljning har egna sidor. Startsidan hjälper er att välja rätt väg.
            </p>
          </RevealItem>
          <RevealStagger className="mt-9 grid gap-5 md:grid-cols-3" stagger={PUBLIC_MOTION.staggerCard}>
            {audienceCards.map((card) => (
              <RevealItem key={card.to} tone="card">
                <article
                  className="relative block rounded-2xl border border-slate-200 bg-slate-50 p-6 transition hover:border-amber-300 hover:bg-white hover:shadow-sm motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
                >
                  <h3 className="text-xl font-bold text-slate-900"><Link to={card.to} className="after:absolute after:inset-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500">{card.title}</Link></h3>
                  <p className="mt-3 text-slate-700 leading-relaxed">{card.text}</p>
                </article>
              </RevealItem>
            ))}
          </RevealStagger>
        </RevealGroup>
      </section>

      <HowItWorksSection />

      <section className="bg-white pb-16 text-center">
        <Reveal>
          <Link
            to="/sa-fungerar-salespartner"
            className="inline-flex font-semibold text-slate-900 underline decoration-amber-500 decoration-2 underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
          >
            Se hur hela flödet fungerar steg för steg
          </Link>
        </Reveal>
      </section>

      <BenefitsSection />
      <EarningsFactsSection />

      <section className="bg-white py-14 md:py-16">
        <RevealGroup className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealItem tone="text" as="h2" className="text-2xl md:text-3xl font-bold text-slate-900">
            Räkna på förtjänsten och läs mer
          </RevealItem>
          <RevealItem tone="text" as="p" className="mt-4 max-w-3xl text-neutral-600 leading-relaxed">
            Provisionen är produkt- och variantbunden och kan variera. Faktisk förtjänst baseras på den
            faktiska provisionen på sålda produkter. Returer och återbetalningar kan påverka
            slutbeloppet. Kampanjen stängs vid periodens slut. Efter att alla order levererats och
            14 dagars väntetid passerat kan förtjänsten betalas ut.
          </RevealItem>
          <RevealStagger className="mt-7 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" stagger={PUBLIC_MOTION.staggerCard}>
            {furtherReadingLinks.map((link) => (
              <RevealItem key={link.to} tone="card">
                <Link
                  to={link.to}
                  className="block rounded-2xl border border-slate-200 bg-slate-50 p-5 font-semibold text-slate-800 transition hover:border-amber-300 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
                >
                  {link.label}
                </Link>
              </RevealItem>
            ))}
          </RevealStagger>
        </RevealGroup>
      </section>

      <section className="bg-slate-950 py-16 text-white">
        <RevealGroup className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <RevealItem tone="text">
            <SectionHeading
              eyebrow="Nästa steg"
              title="Registrera laget eller klassen för godkännande"
              description="Välj rätt upplägg först, eller skicka in en ansökan för ett lag eller en klass."
              align="center"
              className="[&_p]:text-white/75 [&_h2]:text-white [&_[data-eyebrow]]:text-amber-300"
            />
          </RevealItem>
          <RevealStagger className="mt-8 flex flex-col sm:flex-row justify-center gap-3" stagger={PUBLIC_MOTION.staggerCard}>
            <RevealItem tone="card" className="w-full sm:w-auto">
              <Button
                as={Link}
                to="/register-group"
                size="lg"
                className="w-full justify-center bg-amber-500 hover:bg-amber-400 text-slate-950 transition-[color,background-color,border-color,box-shadow,transform] duration-200 motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-md focus-visible:ring-2 focus-visible:ring-amber-300 sm:w-auto"
              >
                Registrera er grupp
              </Button>
            </RevealItem>
            <RevealItem tone="card" className="w-full sm:w-auto">
              <Button
                as={Link}
                to="/contact"
                variant="outline"
                size="lg"
                className="w-full justify-center border-white/40 text-white hover:bg-white/10 transition-[color,background-color,border-color,box-shadow,transform] duration-200 motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-md focus-visible:ring-2 focus-visible:ring-white sm:w-auto"
              >
                Kontakta oss
              </Button>
            </RevealItem>
          </RevealStagger>
        </RevealGroup>
      </section>
    </div>
  );
}
